import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import {
  FaExclamationCircle,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUser,
  FaTimes,
  FaClipboardList,
  FaClock,
} from "react-icons/fa";
import { db } from "../firebase";

const COLORS = {
  primary: "#f20c53",
  secondary: "#fb6c33",
  text: "#0f172a",
  subtext: "#64748b",
  border: "#e2e8f0",
  page: "#f5f6f8",
};

export default function BookedEvents() {
  const user = safeGetEmployee();
  const isSuperAdmin = String(user?.role || "").toLowerCase() === "super_admin";
  const hasPermission = isSuperAdmin || user?.permissions?.includes("view_booked_events");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!hasPermission) return undefined;

    const qRef = query(
      collection(db, "new_event_booking_recived"),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setRows(list);
        setLoading(false);
      },
      (err) => {
        console.error("new_event_booking_recived load failed", err);
        setError(err?.message || "Failed to load booked events");
        setRows([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [hasPermission]);

  const stats = useMemo(() => {
    const live = rows.filter((x) => String(x.status || "").toLowerCase() === "live").length;
    return {
      total: rows.length,
      live,
      hold: rows.length - live,
    };
  }, [rows]);

  if (!hasPermission) {
    return (
      <div style={deniedWrap}>
        <div style={deniedCard}>
          <FaExclamationCircle size={32} color="#e53935" />
          <h3 style={{ marginTop: "1rem" }}>Permission Denied</h3>
          <p style={{ color: "#666" }}>You do not have access to view booked events.</p>
          <button onClick={() => window.history.back()} style={ghostBtn}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="aa-pro-page" style={pageWrap}>
      <style>{responsiveStyles}</style>

      <div className="aa-pro-hero" style={hero}>
        <div style={heroTitleWrap}>
          <div className="aa-pro-hero-icon"><FaClipboardList /></div>
          <div>
            <h2 className="aa-pro-title">Booked Events</h2>
            <p className="aa-pro-subtitle">Real-time bookings from `new_event_booking_recived`.</p>
          </div>
        </div>

        <div className="aa-pro-metrics" style={statsWrap}>
          <Stat label="Total" value={stats.total} />
          <Stat label="Live" value={stats.live} />
          <Stat label="Hold" value={stats.hold} />
        </div>
      </div>

      {loading ? <div style={noteBox}>Loading booked events...</div> : null}
      {!loading && error ? <div style={errorBox}>{error}</div> : null}
      {!loading && !error && !rows.length ? (
        <div style={noteBox}>No booked events found.</div>
      ) : null}

      {!loading && !error && rows.length ? (
        <div className="booked-events-grid">
          {rows.map((row) => (
            <button
              type="button"
              key={row.id}
              style={cardBtn}
              onClick={() => setActive(row)}
              title="Open booking details"
            >
              <div style={cardHead}>
                <div style={cardTitle}>{eventTitle(row)}</div>
                <span style={statusChip(row.status)}>{String(row.status || "hold")}</span>
              </div>

              <div style={cardRow}>
                <FaUser />
                <span>{bookerName(row)}</span>
              </div>
              <div style={cardRow}>
                <FaCalendarAlt />
                <span>{eventDateText(row)}</span>
              </div>
              <div style={cardRow}>
                <FaClock />
                <span>{eventTimeText(row)}</span>
              </div>
              <div style={cardRow}>
                <FaMapMarkerAlt />
                <span>{eventVenue(row)}</span>
              </div>

              <div style={cardFooter}>Booking ID: {row.id}</div>
            </button>
          ))}
        </div>
      ) : null}

      {active ? (
        <div style={modalOverlay} role="dialog" aria-modal="true" aria-label="Booking details">
          <div style={modalCard}>
            <div style={modalHeader}>
              <div>
                <div style={{ fontWeight: 900, fontSize: 17 }}>{eventTitle(active)}</div>
                <div style={{ fontSize: 12, opacity: 0.9 }}>Booking ID: {active.id}</div>
              </div>
              <button type="button" onClick={() => setActive(null)} style={closeBtn}>
                <FaTimes /> Close
              </button>
            </div>

            <div style={modalBody}>
              <DataGrid data={active} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="aa-pro-metric" style={statCard}>
      <div style={statLabel}>{label}</div>
      <div style={statValue}>{value}</div>
    </div>
  );
}

function DataGrid({ data }) {
  const entries = Object.entries(data || {}).sort(([a], [b]) => a.localeCompare(b));
  return (
    <div style={dataGrid}>
      {entries.map(([key, value]) => (
        <div key={key} style={dataRow}>
          <div style={dataKey}>{humanizeKey(key)}</div>
          <div style={dataVal}><DataValue value={value} /></div>
        </div>
      ))}
    </div>
  );
}

function DataValue({ value }) {
  if (value == null) return <span style={muted}>-</span>;

  if (typeof value?.toDate === "function" || value?.seconds != null) {
    return <span>{formatDate(value)}</span>;
  }

  if (Array.isArray(value)) {
    if (!value.length) return <span style={muted}>[]</span>;
    return (
      <div style={{ display: "grid", gap: 8 }}>
        {value.map((item, idx) => (
          <div key={idx} style={arrayItem}>
            <div style={arrayIndex}>#{idx + 1}</div>
            <DataValue value={item} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === "object") {
    const entries = Object.entries(value);
    if (!entries.length) return <span style={muted}>{"{}"}</span>;
    return (
      <div style={nestedBox}>
        {entries.map(([k, v]) => (
          <div key={k} style={nestedRow}>
            <div style={nestedKey}>{humanizeKey(k)}</div>
            <div style={nestedVal}><DataValue value={v} /></div>
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === "boolean") return <span>{value ? "True" : "False"}</span>;

  const text = String(value);
  if (isLikelyUrl(text)) {
    if (isImageUrl(text)) {
      return (
        <a href={text} target="_blank" rel="noreferrer" style={linkStyle}>
          <img src={text} alt="media" style={imagePreview} />
          Open image
        </a>
      );
    }
    if (isVideoUrl(text)) {
      return (
        <a href={text} target="_blank" rel="noreferrer" style={linkStyle}>
          <video src={text} controls style={videoPreview} />
          Open video
        </a>
      );
    }
    return (
      <a href={text} target="_blank" rel="noreferrer" style={linkStyle}>
        {text}
      </a>
    );
  }

  return <span style={{ wordBreak: "break-word" }}>{text}</span>;
}

function eventTitle(row = {}) {
  return (
    row.eventName ||
    row.event_title ||
    row.title ||
    row.event?.name ||
    row.event?.title ||
    "Untitled Event"
  );
}

function eventVenue(row = {}) {
  return (
    row.venueName ||
    row.venue ||
    row.location ||
    row.city ||
    row.event?.venueName ||
    row.event?.location ||
    "-"
  );
}

function eventDateText(row = {}) {
  const candidate = row.date || row.eventDate || row.bookingDate || row.event?.date || row.createdAt;
  return formatDate(candidate);
}

function eventTimeText(row = {}) {
  const from = row.timeFrom || row.startTime || row.from || row.event?.timeFrom;
  const to = row.timeTo || row.endTime || row.to || row.event?.timeTo;
  if (from && to) return `${from} - ${to}`;
  return from || to || "-";
}

function bookerName(row = {}) {
  return (
    row.userName ||
    row.customerName ||
    row.name ||
    row.bookedBy ||
    row.user?.name ||
    row.user?.fullName ||
    "Unknown User"
  );
}

function humanizeKey(key = "") {
  const plain = String(key || "").replace(/[_-]+/g, " ").trim();
  if (!plain) return "-";
  return plain.replace(/\b\w/g, (x) => x.toUpperCase());
}

function safeGetEmployee() {
  try {
    const raw = localStorage.getItem("employee");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function toMillis(v) {
  if (!v) return 0;
  if (typeof v?.toMillis === "function") return v.toMillis();
  if (typeof v?.toDate === "function") return v.toDate().getTime();
  if (typeof v?.seconds === "number") return v.seconds * 1000;
  const parsed = Date.parse(String(v));
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDate(v) {
  const ms = toMillis(v);
  if (!ms) {
    const raw = String(v || "").trim();
    return raw || "-";
  }
  return new Date(ms).toLocaleString("en-IN");
}

function isLikelyUrl(value = "") {
  return /^https?:\/\//i.test(String(value || ""));
}

function isImageUrl(value = "") {
  return /\.(png|jpe?g|webp|gif|avif|svg)(\?|$)/i.test(String(value || ""));
}

function isVideoUrl(value = "") {
  return /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(String(value || ""));
}

function statusChip(status) {
  const v = String(status || "").toLowerCase();
  if (v === "live" || v === "approved" || v === "confirmed") {
    return { ...chipBase, background: "#ecfdf5", color: "#047857", border: "1px solid #bbf7d0" };
  }
  if (v === "cancelled" || v === "rejected") {
    return { ...chipBase, background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" };
  }
  return { ...chipBase, background: "#fff7ed", color: "#b45309", border: "1px solid #fed7aa" };
}

const responsiveStyles = `
  .booked-events-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }

  @media (max-width: 1100px) {
    .booked-events-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 700px) {
    .booked-events-grid {
      grid-template-columns: 1fr;
    }
  }
`;

const pageWrap = {
  padding: "1rem",
  fontFamily: "Inter, system-ui, sans-serif",
  background: COLORS.page,
  minHeight: "100%",
};

const hero = {
  borderRadius: 14,
  background: `linear-gradient(100deg, ${COLORS.primary}, ${COLORS.secondary})`,
  color: "#fff",
  padding: "14px",
  marginBottom: 14,
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
  boxShadow: "0 16px 26px rgba(15,23,42,0.18)",
};

const heroTitleWrap = { display: "flex", gap: 10, alignItems: "center" };
const heroIcon = {
  width: 42,
  height: 42,
  borderRadius: 10,
  display: "grid",
  placeItems: "center",
  border: "1px solid rgba(255,255,255,0.35)",
  background: "rgba(255,255,255,0.16)",
};
const heroTitle = { margin: 0, fontWeight: 900, fontSize: 24 };
const heroSub = { margin: "4px 0 0", fontSize: 13, opacity: 0.95 };

const statsWrap = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" };
const statCard = {
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.45)",
  background: "rgba(255,255,255,0.16)",
  padding: "8px 10px",
  minWidth: 80,
  textAlign: "center",
};
const statLabel = { fontSize: 11, opacity: 0.95 };
const statValue = { fontSize: 18, fontWeight: 900, lineHeight: 1.1 };

const cardBtn = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  background: "#fff",
  color: COLORS.text,
  padding: "12px",
  textAlign: "left",
  display: "grid",
  gap: 8,
  boxShadow: "0 10px 18px rgba(15,23,42,0.08)",
  cursor: "pointer",
};

const cardHead = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 };
const cardTitle = { fontSize: 15, fontWeight: 900, lineHeight: 1.25 };
const chipBase = {
  borderRadius: 999,
  padding: "3px 8px",
  fontSize: 11,
  fontWeight: 800,
  textTransform: "capitalize",
  whiteSpace: "nowrap",
};
const cardRow = { display: "inline-flex", alignItems: "center", gap: 7, color: COLORS.subtext, fontSize: 13 };
const cardFooter = {
  marginTop: 4,
  borderTop: `1px dashed ${COLORS.border}`,
  paddingTop: 6,
  fontSize: 12,
  color: COLORS.subtext,
};

const noteBox = {
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  padding: "12px",
  color: COLORS.subtext,
};

const errorBox = {
  ...noteBox,
  border: "1px solid #fecaca",
  color: "#b91c1c",
  background: "#fff1f2",
};

const deniedWrap = {
  backdropFilter: "blur(5px)",
  backgroundColor: "rgba(0,0,0,0.3)",
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 999,
};

const deniedCard = {
  background: "#fff",
  padding: "2rem",
  borderRadius: "12px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  textAlign: "center",
};

const ghostBtn = {
  marginTop: "1.5rem",
  backgroundColor: "#555",
  color: "#fff",
  padding: "0.6rem 1.2rem",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
};

const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(2,6,23,0.58)",
  display: "grid",
  placeItems: "center",
  zIndex: 9999,
  padding: 14,
};

const modalCard = {
  width: "min(980px, 96vw)",
  maxHeight: "88vh",
  borderRadius: 14,
  overflow: "hidden",
  background: "#fff",
  boxShadow: "0 28px 56px rgba(2,6,23,0.35)",
  display: "grid",
  gridTemplateRows: "58px 1fr",
};

const modalHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  color: "#fff",
  padding: "10px 12px",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
};

const closeBtn = {
  border: "1px solid rgba(255,255,255,0.45)",
  color: "#fff",
  background: "transparent",
  borderRadius: 8,
  height: 34,
  padding: "0 10px",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  cursor: "pointer",
  fontWeight: 700,
};

const modalBody = {
  overflow: "auto",
  padding: 12,
  background: "#f8fafc",
};

const dataGrid = { display: "grid", gap: 10 };
const dataRow = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  background: "#fff",
  overflow: "hidden",
};
const dataKey = {
  padding: "8px 10px",
  borderBottom: `1px solid ${COLORS.border}`,
  background: "#f8fafc",
  fontWeight: 800,
  color: COLORS.text,
  fontSize: 13,
};
const dataVal = { padding: "10px" };

const nestedBox = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  overflow: "hidden",
};
const nestedRow = {
  display: "grid",
  gridTemplateColumns: "220px 1fr",
  borderBottom: `1px solid ${COLORS.border}`,
};
const nestedKey = {
  padding: "8px",
  fontWeight: 700,
  background: "#f8fafc",
  color: COLORS.text,
  borderRight: `1px solid ${COLORS.border}`,
};
const nestedVal = { padding: "8px", minHeight: 38 };

const arrayItem = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  overflow: "hidden",
};
const arrayIndex = {
  padding: "6px 8px",
  fontWeight: 800,
  fontSize: 12,
  background: "#f8fafc",
  borderBottom: `1px solid ${COLORS.border}`,
};

const linkStyle = { color: "#1d4ed8", textDecoration: "none", display: "inline-grid", gap: 6 };
const imagePreview = { width: "min(260px, 100%)", borderRadius: 8, border: `1px solid ${COLORS.border}` };
const videoPreview = { width: "min(300px, 100%)", borderRadius: 8, border: `1px solid ${COLORS.border}` };
const muted = { color: COLORS.subtext };

